import { Flex, Divider, useColorModeValue, Box, Link } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';

import type { Query } from 'nextjs-routes';

import config from 'configs/app';
import { SITE_MAP_OLD_UI } from 'lib/consts';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';

import DeFiDropdown from './DeFiDropdown';
import NetworkMenu from './NetworkMenu';
import Settings from './settings/Settings';
import TopBarStats from './TopBarStats';

const getOldSiteUrl = (currentPath: string, query: Query) => {
  if (SITE_MAP_OLD_UI[currentPath as never]) {
    let oldUrl: string = SITE_MAP_OLD_UI[currentPath as never] as string;

    for (const [ key, value ] of Object.entries(query)) {
      oldUrl = oldUrl.replace(`[${key}]`, value as string);
    }

    return oldUrl;
  }

  return null; // No matching route found
};

const TopBar = () => {
  const bgColor = useColorModeValue('gray.50', 'whiteAlpha.100');
  const { query, pathname } = useRouter();

  const oldWebsiteLink = useMemo(() => {
    const newLink = getOldSiteUrl(pathname, query);
    if (!newLink) {
      return SITE_MAP_OLD_UI['/'];
    }

    return newLink;
  }, [ query, pathname ]);

  return (
    <Box bgColor={ bgColor }>
      <Flex
        py={ 2 }
        px={{ base: 3, lg: 6 }}
        maxW={ `${ CONTENT_MAX_WIDTH }px` }
        m="0 auto"
        justifyContent="space-between"
        alignItems="center"
      >
        <TopBarStats/>
        <Flex alignItems="center">
          { config.features.deFiDropdown.isEnabled && (
            <>
              <DeFiDropdown/>
              <Divider mr={ 3 } ml={{ base: 2, sm: 3 }} height={ 4 } orientation="vertical"/>
            </>
          ) }
          <Link
            href={oldWebsiteLink}
            fontSize="sm"
            bgColor="magenta.500"
            rounded={ 4 }
            px={ 6 }
            textDecor="none"
            mr="2"
            color="white"
            _hover={ { bgColor: 'magenta.400' } }
          >
            Old UI
          </Link>
          <Divider mr={ 3 } ml={{ base: 2, sm: 3 }} height={ 4 } orientation="vertical"/>
          <Settings/>
          { config.UI.navigation.layout === 'horizontal' && Boolean(config.UI.navigation.featuredNetworks) && (
            <Box display={{ base: 'none', lg: 'flex' }}>
              <Divider mx={ 3 } height={ 4 } orientation="vertical"/>
              <NetworkMenu/>
            </Box>
          ) }
        </Flex>
      </Flex>
    </Box>
  );
};

export default React.memo(TopBar);
